// BrandingEditor — form controlado sobre un BrandingConfig.
//
// Features M9.5:
//   - 2 grupos de color pickers (light + dark palette opcional).
//   - Contrast checker WCAG en tiempo real para los pares criticos
//     (text / background, text-muted / surface, white / primary).
//   - Preview rapido con las CSS variables aplicadas en un div local.
//   - Validacion basica (url, font min).
//
// El contrast checker NO bloquea submit — solo advierte. El partner final
// decide si pasa WCAG o no.

import { useMemo } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import type { BrandingConfig, BrandingPalette } from '@/lib/api';
import { contrastRatio } from '@/lib/contrast';
import { fieldCls } from '@/lib/form-styles';

interface BrandingEditorProps {
  value: BrandingConfig;
  onChange: (value: BrandingConfig) => void;
}

const PALETTE_FIELDS: Array<{ key: keyof BrandingPalette; label: string }> = [
  { key: 'primary', label: 'Primary' },
  { key: 'accent', label: 'Accent' },
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'text', label: 'Text' },
  { key: 'text_muted', label: 'Text muted' },
  { key: 'error', label: 'Error' },
  // Opcional (branding viejo no lo tiene): vacío = el login-portal usa su
  // verde default por tema. Se usa en confirmaciones (ej: corrección del
  // typo de email en el signup).
  { key: 'success', label: 'Success' },
];

function ContrastBadge({ fg, bg, label }: { fg: string; bg: string; label: string }): JSX.Element {
  const res = contrastRatio(fg, bg);
  if (!res) {
    return (
      <span className="text-xs text-ink-muted">
        {label}: no calculable
      </span>
    );
  }
  const status = res.passes_aa
    ? { text: 'AA', cls: 'text-status-success', Icon: Check }
    : res.passes_aa_large
      ? { text: 'AA large', cls: 'text-status-warning', Icon: AlertTriangle }
      : { text: 'FAIL', cls: 'text-status-error', Icon: X };
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${status.cls}`} title={`${res.ratio}:1`}>
      {label}: {res.ratio}:1 {status.text}
      <status.Icon size={11} className="shrink-0" aria-hidden="true" />
    </span>
  );
}

function PaletteBlock({
  title,
  palette,
  onChange,
}: {
  title: string;
  palette: BrandingPalette;
  onChange: (next: BrandingPalette) => void;
}): JSX.Element {
  const update = (key: keyof BrandingPalette, value: string): void => {
    // `success` es opcional: si lo vacían, se ELIMINA la key (el schema
    // rechaza '' y ausente significa "usar el default del login-portal").
    if (key === 'success' && value.trim() === '') {
      const next = { ...palette };
      delete next.success;
      onChange(next);
      return;
    }
    onChange({ ...palette, [key]: value });
  };

  return (
    <fieldset className="border border-surface-border dark:border-surface-dark-border rounded-xl p-4 space-y-3">
      <legend className="px-2 text-sm font-medium">{title}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PALETTE_FIELDS.map((f) => (
          <label key={f.key} className="text-sm space-y-1">
            <span>{f.label}</span>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={palette[f.key]?.startsWith('#') && palette[f.key]?.length === 7 ? palette[f.key] : '#000000'}
                onChange={(e) => update(f.key, e.target.value)}
                className="h-9 w-12 rounded border border-surface-border dark:border-surface-dark-border cursor-pointer"
              />
              <input
                type="text"
                value={palette[f.key] ?? ''}
                placeholder={f.key === 'success' ? 'default (verde del portal)' : undefined}
                onChange={(e) => update(f.key, e.target.value)}
                className={fieldCls({ size: 'sm', mono: true, block: false, extra: 'flex-1' })}
              />
            </div>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t border-surface-border dark:border-surface-dark-border">
        <ContrastBadge fg={palette.text} bg={palette.background} label="Text / bg" />
        <ContrastBadge fg={palette.text} bg={palette.surface} label="Text / surface" />
        <ContrastBadge fg={palette.text_muted} bg={palette.surface} label="Muted / surface" />
        <ContrastBadge fg="#ffffff" bg={palette.primary} label="White / primary" />
        <ContrastBadge fg={palette.error} bg={palette.background} label="Error / bg" />
        {palette.success ? (
          <ContrastBadge fg={palette.success} bg={palette.background} label="Success / bg" />
        ) : null}
      </div>
    </fieldset>
  );
}

export default function BrandingEditor({ value, onChange }: BrandingEditorProps): JSX.Element {
  const hasDark = !!value.colors_dark;

  const previewStyle = useMemo(
    () => ({
      ['--bg' as string]: value.colors.background,
      ['--surface' as string]: value.colors.surface,
      ['--text' as string]: value.colors.text,
      ['--muted' as string]: value.colors.text_muted,
      ['--primary' as string]: value.colors.primary,
      ['--accent' as string]: value.colors.accent,
    }),
    [value.colors],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm space-y-1">
          <span className="font-medium">Display name</span>
          <input
            type="text"
            value={value.display_name}
            onChange={(e) => onChange({ ...value, display_name: e.target.value })}
            className={fieldCls({ size: 'lg' })}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Theme mode</span>
          <select
            value={value.theme_mode}
            onChange={(e) =>
              onChange({
                ...value,
                theme_mode: e.target.value as BrandingConfig['theme_mode'],
              })
            }
            className={fieldCls({ size: 'lg' })}
          >
            <option value="auto">auto (respeta prefers-color-scheme)</option>
            <option value="light">light (fijo)</option>
            <option value="dark">dark (fijo)</option>
          </select>
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Logo URL</span>
          <input
            type="url"
            value={value.logo_url}
            onChange={(e) => onChange({ ...value, logo_url: e.target.value })}
            className={fieldCls({ size: 'lg' })}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Logo URL (dark)</span>
          <input
            type="url"
            value={value.logo_dark_url ?? ''}
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.logo_dark_url = e.target.value;
              else delete next.logo_dark_url;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg' })}
          />
        </label>
        <label className="text-sm space-y-1 md:col-span-2">
          <span className="font-medium">Favicon URL</span>
          <input
            type="url"
            value={value.favicon_url ?? ''}
            placeholder="https://assets.example.com/favicon.ico"
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.favicon_url = e.target.value;
              else delete next.favicon_url;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg' })}
          />
          <span className="text-xs text-ink-muted">
            Opcional. Se inyecta como &lt;link rel=&quot;icon&quot;&gt; en el login portal.
          </span>
        </label>
        <label className="text-sm space-y-1 md:col-span-2">
          <span className="font-medium">Font family</span>
          <input
            type="text"
            value={value.font_family}
            onChange={(e) => onChange({ ...value, font_family: e.target.value })}
            className={fieldCls({ size: 'lg', mono: true })}
          />
          <span className="text-xs text-ink-muted">
            Stack CSS — incluí fallbacks (ej: <code>'Inter', system-ui, sans-serif</code>).
          </span>
        </label>
        <label className="text-sm space-y-1 md:col-span-2">
          <span className="font-medium">Font URL (CSS stylesheet)</span>
          <input
            type="url"
            value={value.font_url ?? ''}
            placeholder="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.font_url = e.target.value;
              else delete next.font_url;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg', mono: true })}
          />
          <span className="text-xs text-ink-muted">
            Opcional. URL al CSS de la fuente (Google Fonts, Bunny Fonts, CDN propio). Si se omite, la fuente solo se aplica si ya está instalada en el sistema del usuario.
          </span>
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Términos y Condiciones (URL)</span>
          <input
            type="url"
            value={value.tos_url ?? ''}
            placeholder="https://partner.com/terminos"
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.tos_url = e.target.value;
              else delete next.tos_url;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg' })}
          />
          <span className="text-xs text-ink-muted">
            Link al T&C. Aparece junto al checkbox obligatorio del signup.
          </span>
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Privacidad (URL)</span>
          <input
            type="url"
            value={value.privacy_url ?? ''}
            placeholder="https://partner.com/privacidad"
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.privacy_url = e.target.value;
              else delete next.privacy_url;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg' })}
          />
          <span className="text-xs text-ink-muted">
            Link a la Política de Privacidad. Mismo lugar que T&C.
          </span>
        </label>
        <div className="md:col-span-2 space-y-2 rounded border border-surface-border dark:border-surface-dark-border p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={value.contest_active ?? false}
              onChange={(e) => {
                const next: BrandingConfig = { ...value };
                if (e.target.checked) next.contest_active = true;
                else delete next.contest_active;
                onChange(next);
              }}
            />
            Concurso / sorteo activo
          </label>
          <span className="block text-xs text-ink-muted">
            Si está tildado (con nombre + URL), el checkbox de términos del signup agrega
            “… y {value.contest_name || '{Nombre concurso}'}” con link a sus bases. Si no, muestra
            solo Términos y Condiciones + Política de Privacidad.
          </span>
          <label className="block text-sm space-y-1">
            <span className="font-medium">Nombre del concurso</span>
            <input
              type="text"
              value={value.contest_name ?? ''}
              placeholder="Ej: Modo Mundial"
              maxLength={120}
              onChange={(e) => {
                const next: BrandingConfig = { ...value };
                if (e.target.value) next.contest_name = e.target.value;
                else delete next.contest_name;
                onChange(next);
              }}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label className="block text-sm space-y-1">
            <span className="font-medium">Bases del concurso (URL)</span>
            <input
              type="url"
              value={value.contest_terms_url ?? ''}
              placeholder="https://partner.com/bases-concurso"
              onChange={(e) => {
                const next: BrandingConfig = { ...value };
                if (e.target.value) next.contest_terms_url = e.target.value;
                else delete next.contest_terms_url;
                onChange(next);
              }}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
        </div>
        <label className="text-sm space-y-1 md:col-span-2">
          <span className="font-medium">Footer HTML</span>
          <textarea
            value={value.footer_html ?? ''}
            placeholder='<p>© 2026 · <a href="https://example.com/terminos">Términos</a></p>'
            maxLength={4000}
            rows={4}
            onChange={(e) => {
              const next: BrandingConfig = { ...value };
              if (e.target.value) next.footer_html = e.target.value;
              else delete next.footer_html;
              onChange(next);
            }}
            className={fieldCls({ size: 'lg', mono: true })}
          />
          <span className="text-xs text-status-warning">
            Opcional, máximo 4000 caracteres. Se renderiza tal cual en el footer del login (sin sanitizar).
            Limitate a HTML simple — links, párrafos. No incluyas &lt;script&gt;, &lt;iframe&gt; ni event handlers inline.
          </span>
        </label>
      </div>

      <PaletteBlock
        title="Paleta light"
        palette={value.colors}
        onChange={(p) => onChange({ ...value, colors: p })}
      />

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="has-dark"
          checked={hasDark}
          onChange={(e) => {
            const next = { ...value };
            if (e.target.checked && !next.colors_dark) {
              next.colors_dark = { ...value.colors };
            } else if (!e.target.checked) {
              delete next.colors_dark;
            }
            onChange(next);
          }}
        />
        <label htmlFor="has-dark" className="text-sm">
          Configurar paleta dark (recomendado si theme_mode = auto)
        </label>
      </div>

      {hasDark && value.colors_dark ? (
        <PaletteBlock
          title="Paleta dark"
          palette={value.colors_dark}
          onChange={(p) => onChange({ ...value, colors_dark: p })}
        />
      ) : null}

      <section aria-labelledby="preview-title" className="space-y-2">
        <h3 id="preview-title" className="text-sm font-medium text-ink-muted">
          Preview
        </h3>
        <div
          className="rounded-xl border p-6 space-y-3"
          style={{
            ...previewStyle,
            background: 'var(--bg)',
            color: 'var(--text)',
            borderColor: value.colors.surface,
          }}
        >
          <div
            className="rounded-lg p-5 space-y-3"
            style={{ background: 'var(--surface)' }}
          >
            <p style={{ color: 'var(--text)', fontWeight: 600 }}>
              Ingresar en {value.display_name}
            </p>
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>
              Usá tu email y contraseña.
            </p>
            <div
              className="rounded px-4 py-2 text-center text-white font-medium"
              style={{ background: 'var(--primary)' }}
            >
              Ingresar
            </div>
            <p
              className="underline text-sm"
              style={{ color: 'var(--accent)' }}
            >
              ¿Olvidaste tu contraseña?
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
